import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, ChevronUp, Inbox, CalendarDays } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "under_review", "quote_sent", "manual_review", "accepted_awaiting_payment", "payment_expired", "paid_scheduling_required", "scheduled", "in_progress", "completed", "cancelled"];
const LABELS = {
  new: "New", under_review: "Under Review", quote_sent: "Quote Sent", manual_review: "Manual Review",
  accepted_awaiting_payment: "Accepted — Awaiting Payment", payment_expired: "Payment Expired",
  paid_scheduling_required: "Paid — Scheduling Required",
  scheduled: "Scheduled", in_progress: "In Progress", completed: "Completed", cancelled: "Cancelled",
};
const STATUS_COLOR = {
  new: "text-blue-300 bg-blue-400/10", under_review: "text-purple-300 bg-purple-400/10", quote_sent: "text-sky-300 bg-sky-400/10",
  manual_review: "text-amber-300 bg-amber-400/10", accepted_awaiting_payment: "text-orange-300 bg-orange-400/10",
  payment_expired: "text-rose-300 bg-rose-400/10",
  paid_scheduling_required: "text-teal-300 bg-teal-400/10", scheduled: "text-emerald-300 bg-emerald-400/10",
  in_progress: "text-cyan-300 bg-cyan-400/10", completed: "text-green-300 bg-green-400/10", cancelled: "text-red-300 bg-red-400/10",
};
const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const SLOT_STARTS = ["08:00", "10:00", "12:00", "14:00"];
const money = (n) => `CA$${Number(n).toLocaleString("en-CA", { minimumFractionDigits: 2 })}`;
const fieldCls = "bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-steel-light/60";

function Row({ q, auth, onChanged }) {
  const [open, setOpen] = useState(false);
  const [finalPrice, setFinalPrice] = useState(q.final_price ?? "");
  const [apDate, setApDate] = useState(q.appointment?.date || "");
  const [apStart, setApStart] = useState(q.appointment?.start || "08:00");
  const price = q.final_price ?? q.estimate;

  const patch = async (body, okMsg) => {
    try {
      await axios.patch(`${API}/admin/quote-requests/${q.id}`, body, auth);
      toast.success(okMsg);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className="border-b border-white/6">
      <div className="grid grid-cols-2 md:grid-cols-7 gap-3 items-center px-5 py-4 hover:bg-white/[0.02]">
        <div>
          <div className="text-steel-light font-600 text-sm">{q.reference_id}</div>
          <div className="text-white/35 text-xs">{new Date(q.created_at).toLocaleDateString()}</div>
        </div>
        <div className="min-w-0">
          <div className="text-white text-sm truncate">{q.full_name}</div>
          <div className="text-white/40 text-xs truncate">{q.email}</div>
        </div>
        <div className="text-white/70 text-sm truncate">{q.service}</div>
        <div className="text-white text-sm tabular-nums">
          {money(price)}
          {q.final_price != null && <span className="text-white/35 text-xs block">final (est. {money(q.estimate)})</span>}
        </div>
        <div>
          <select data-testid={`quote-status-${q.reference_id}`} value={q.status}
            onChange={(e) => patch({ status: e.target.value }, `Status → ${LABELS[e.target.value]}`)}
            className={`rounded-full px-3 py-1.5 text-xs border-0 appearance-none cursor-pointer ${STATUS_COLOR[q.status] || "text-white/60 bg-white/5"}`}>
            {STATUSES.map((s) => <option key={s} value={s} className="bg-ink-800 text-white">{LABELS[s]}</option>)}
          </select>
        </div>
        <div className="text-white/60 text-xs">
          {q.appointment ? <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-steel-light" />{q.appointment.date} {q.appointment.start}</span>
            : q.held_appointment ? <span className="inline-flex items-center gap-1.5 text-amber-300/80"><CalendarDays className="h-3.5 w-3.5" />{q.held_appointment.date} {q.held_appointment.start} (held)</span>
            : "—"}
          {q.needs_manual_review && <span className="inline-flex items-center gap-1 ml-2 text-amber-300"><AlertTriangle className="h-3 w-3" />review</span>}
        </div>
        <button data-testid={`quote-expand-${q.reference_id}`} onClick={() => setOpen(!open)} className="text-white/40 hover:text-white justify-self-end">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>
      {open && (
        <div className="px-5 pb-5 grid md:grid-cols-3 gap-5 text-sm">
          <div className="text-white/60 space-y-1 text-xs leading-relaxed">
            <div><span className="text-white/35">Phone:</span> {q.phone} · <span className="text-white/35">Postal:</span> {q.postal_code}</div>
            <div><span className="text-white/35">Property:</span> {q.property_type} · {q.bedrooms ? `${q.bedrooms}bd/${q.bathrooms}ba` : q.sqft ? `${q.sqft} sqft` : q.quantity ? `${q.quantity} units` : ""}</div>
            <div><span className="text-white/35">Frequency:</span> {q.frequency} · <span className="text-white/35">Condition:</span> {q.condition}</div>
            {q.addons?.length > 0 && <div><span className="text-white/35">Add-ons:</span> {q.addons.join(", ")}</div>}
            {q.details && <div className="italic">"{q.details}"</div>}
            {q.review_reasons?.length > 0 && <div className="text-amber-300/80"><AlertTriangle className="h-3 w-3 inline mr-1" />{q.review_reasons.join("; ")}</div>}
            {q.acceptance && <div className="text-emerald-300/70">Accepted {new Date(q.acceptance.accepted_at).toLocaleString()} · {q.acceptance.service_address}</div>}
          </div>
          <div>
            <p className="text-white/40 text-xs mb-2">Final price (CA$, before tax) — for manual-review quotes</p>
            <div className="flex gap-2">
              <input data-testid={`final-price-input-${q.reference_id}`} type="number" min="0" step="5" className={fieldCls + " w-28"} value={finalPrice} onChange={(e) => setFinalPrice(e.target.value)} />
              <button data-testid={`final-price-save-${q.reference_id}`} onClick={() => finalPrice !== "" && patch({ final_price: Number(finalPrice), status: "quote_sent" }, "Final price set — quote-ready email sent")}
                className="btn-pill bg-white text-ink-950 px-4 py-2 text-xs font-600">Set & Send Quote</button>
            </div>
          </div>
          <div>
            <p className="text-white/40 text-xs mb-2">Set / override appointment (emails both parties)</p>
            <div className="flex gap-2 flex-wrap">
              <input type="date" className={fieldCls} value={apDate} onChange={(e) => setApDate(e.target.value)} data-testid={`appt-date-${q.reference_id}`} />
              <select className={fieldCls} value={apStart} onChange={(e) => setApStart(e.target.value)}>
                {SLOT_STARTS.map((s) => <option key={s} value={s} className="bg-ink-800">{s}</option>)}
              </select>
              <button data-testid={`appt-save-${q.reference_id}`} onClick={() => apDate && patch({ appointment_date: apDate, appointment_start: apStart, status: "scheduled" }, "Appointment set")}
                className="btn-pill glass text-white px-4 py-2 text-xs font-600">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function QuotesPanel({ token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [rows, setRows] = useState(null);
  const [days, setDays] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    axios.get(`${API}/admin/quote-requests`, auth).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [token]); // eslint-disable-line
  useEffect(() => {
    load();
    axios.get(`${API}/admin/scheduling-settings`, auth).then((r) => setDays(r.data)).catch(() => {});
  }, [load]); // eslint-disable-line

  const toggleDay = (k) => {
    const next = { ...days, [k]: !days[k] };
    setDays(next);
    axios.put(`${API}/admin/scheduling-settings`, next, auth)
      .then(() => toast.success("Operating days updated"))
      .catch(() => {
        toast.error("Failed to save");
        axios.get(`${API}/admin/scheduling-settings`, auth).then((r) => setDays(r.data)).catch(() => {});
      });
  };

  if (!rows) return <div className="p-16 text-center text-white/40">Loading…</div>;
  const filtered = filter === "all" ? rows : filter === "review" ? rows.filter((r) => r.needs_manual_review) : rows.filter((r) => r.status === filter);

  return (
    <div className="space-y-6" data-testid="quotes-panel">
      {days && (
        <div className="glass rounded-3xl p-5 flex flex-wrap items-center gap-3" data-testid="operating-days">
          <span className="text-white/50 text-xs uppercase tracking-wide mr-2">Operating days</span>
          {DAYS.map((d) => (
            <button key={d} data-testid={`day-toggle-${d}`} onClick={() => toggleDay(d)}
              className={`px-3.5 py-1.5 rounded-full text-xs border transition-all capitalize ${days[d] ? "bg-white text-ink-950 border-white font-600" : "border-white/15 text-white/40"}`}>
              {d.slice(0, 3)}
            </button>
          ))}
          <span className="text-white/30 text-xs ml-auto">8:00–17:00 · 2h windows · 30 min buffer</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {[["all", `All (${rows.length})`], ["review", `Needs Review Flag (${rows.filter((r) => r.needs_manual_review).length})`],
          ...STATUSES.map((s) => [s, `${LABELS[s]} (${rows.filter((r) => r.status === s).length})`])].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
            className={`btn-pill px-3.5 py-1.5 text-xs ${filter === k ? "bg-white text-ink-950 font-600" : "glass text-white/55"}`}>{label}</button>
        ))}
      </div>

      <div className="glass rounded-3xl overflow-hidden">
        <div className="hidden md:grid grid-cols-7 gap-3 px-5 py-3 border-b border-white/8 text-white/35 text-xs uppercase tracking-wide">
          <span>Quote #</span><span>Customer</span><span>Service</span><span>Price</span><span>Status</span><span>Appointment</span><span />
        </div>
        {filtered.length === 0 ? (
          <div className="p-16 text-center text-white/40"><Inbox className="h-10 w-10 mx-auto mb-4 opacity-40" />No quotes match this filter.</div>
        ) : filtered.map((q) => <Row key={q.id} q={q} auth={auth} onChanged={load} />)}
      </div>
    </div>
  );
}
